<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge } from '@drama/ui'
import { Terminal, Activity, ChevronDown, ChevronRight } from '@lucide/vue'

const props = defineProps({
  record: { type: Object, required: true },
  traceSteps: { type: Array, default: () => [] },
  traceTurns: { type: Array, default: () => [] },
  publicEvents: { type: Array, default: () => [] },
})

const { t } = useI18n()

const showRawEvents = ref(false)
const expandedSteps = ref(new Set())
const expandedTurns = ref(new Set())

// 默认展开最后一轮或有失败的轮次
if (props.traceTurns.length) {
  const failTurn = props.traceTurns.find(tr => tr.hasFail)
  if (failTurn) {
    expandedTurns.value.add(failTurn.turnNumber)
  } else {
    expandedTurns.value.add(props.traceTurns[props.traceTurns.length - 1].turnNumber)
  }
}

function shortHash(text, len = 10) {
  if (!text || typeof text !== 'string') return text
  if (text.length <= len + 4) return text
  return `${text.slice(0, len)}...`
}

function safeValue(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return t('storyFilm.executionLog.notProvided')
  return String(value)
}

function safeJson(value) {
  return JSON.stringify(value, null, 2)
}

function toggleTurn(turnNum) {
  if (expandedTurns.value.has(turnNum)) {
    expandedTurns.value.delete(turnNum)
  } else {
    expandedTurns.value.add(turnNum)
  }
}

function toggleStep(id) {
  if (expandedSteps.value.has(id)) {
    expandedSteps.value.delete(id)
  } else {
    expandedSteps.value.add(id)
  }
}

function stepKindBadge(kind) {
  if (kind === 'run_command' || kind === 'command_execution') return { label: '执行命令', variant: 'info' }
  if (kind === 'edit_file' || kind === 'write_file') return { label: '修改文件', variant: 'warning' }
  if (kind === 'read_file' || kind === 'view_file') return { label: '读取文件', variant: 'neutral' }
  return { label: kind || '执行步骤', variant: 'neutral' }
}
</script>

<template>
  <div class="prep-diagnostics-view">
    <section class="dense-card" data-testid="preparation-agent">
      <div class="dense-card-head">
        <div class="head-left">
          <Terminal :size="14" class="card-glyph" />
          <h4>Agent 执行过程与轮次流水线</h4>
        </div>
        <span class="head-count-sub">{{ traceSteps.length }} 步骤 · {{ traceTurns.length }} 轮次</span>
      </div>

      <!-- 会话出处指示 -->
      <div class="session-brief-bar">
        <span>会话 Thread: <code :title="record.thread_id">{{ shortHash(record.thread_id, 12) }}</code></span>
        <span>工作区 ID: <code>{{ safeValue(record.task?.workspace_id) }}</code></span>
      </div>

      <!-- 按轮次（Turn）折叠展示步骤 -->
      <div class="trace-turns-scroll">
        <div v-for="tGroup in traceTurns" :key="tGroup.turnNumber" class="turn-fold-item">
          <div class="turn-fold-head" @click="toggleTurn(tGroup.turnNumber)">
            <div class="turn-head-l">
              <component :is="expandedTurns.has(tGroup.turnNumber) ? ChevronDown : ChevronRight" :size="14" />
              <span class="turn-tit">轮次 {{ tGroup.turnNumber }} (共 {{ tGroup.stepCount }} 步)</span>
              <span v-if="tGroup.hasFail" class="error-badge">含错误</span>
            </div>
            <span class="turn-head-r">{{ tGroup.steps[tGroup.steps.length - 1]?.kind || '' }}</span>
          </div>

          <!-- 展开的步骤列表 -->
          <div v-show="expandedTurns.has(tGroup.turnNumber)" class="turn-steps-list">
            <div
              v-for="step in tGroup.steps"
              :key="step.id"
              class="step-dense-row"
              :class="{ 'is-expanded': expandedSteps.has(step.id) }"
            >
              <div class="step-line-summary" @click="toggleStep(step.id)">
                <span class="step-seq">S{{ step.number }}</span>
                <Badge :variant="stepKindBadge(step.kind).variant" size="xs">
                  {{ stepKindBadge(step.kind).label }}
                </Badge>
                <span class="step-kind-name">{{ safeValue(step.kind) }}</span>
                <span v-if="step.exit_code != null" class="step-code-pip" :class="step.exit_code === 0 ? 'is-ok' : 'is-fail'">
                  exit {{ step.exit_code }}
                </span>
                <Badge :variant="step.status === 'completed' || step.status === 'ok' ? 'success' : 'danger'" size="xs">
                  {{ safeValue(step.status) }}
                </Badge>
              </div>

              <!-- 变更文件 -->
              <div v-if="Array.isArray(step.files) && step.files.length" class="step-files-strip">
                <span class="files-k">产出文件:</span>
                <code v-for="f in step.files" :key="f" class="file-chip">{{ safeValue(f) }}</code>
              </div>

              <!-- 单步技术下钻 -->
              <pre v-if="expandedSteps.has(step.id)" class="step-pre-box">{{ safeJson(step) }}</pre>
            </div>
          </div>
        </div>
        <p v-if="!traceTurns.length" class="dense-empty-line">{{ t('storyFilmReview.preparationTraceEmpty') }}</p>
      </div>

      <!-- 底层工具事件收纳面板 -->
      <div v-if="record.events" class="dense-events-accordion">
        <div class="events-accordion-head" @click="showRawEvents = !showRawEvents">
          <div class="acc-l">
            <Activity :size="13" />
            <span>底层原始事件采集 (共 {{ publicEvents ? publicEvents.length : 0 }} 条)</span>
          </div>
          <component :is="showRawEvents ? ChevronDown : ChevronRight" :size="13" />
        </div>
        <div v-if="showRawEvents" class="events-compact-list">
          <div v-for="(ev, idx) in publicEvents || []" :key="idx" class="event-dense-item">
            <span class="ev-time-txt">{{ safeValue(ev.timestamp?.slice(11, 19)) }}</span>
            <span class="ev-tool-txt">{{ safeValue(ev.tool || ev.kind) }}</span>
            <span class="ev-res-txt" :class="ev.result_status === 'completed' || ev.result_status === 'ok' ? 'is-ok' : 'is-run'">
              {{ safeValue(ev.result_status) }}
            </span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.prep-diagnostics-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.dense-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  padding: 12px 14px;
}

.dense-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--drama-color-border);
}

.head-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dense-card-head h4 {
  margin: 0;
  font-size: var(--drama-font-size-sm, 14px);
  font-weight: 600;
}

.card-glyph {
  color: var(--drama-color-primary);
}

.head-count-sub {
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
}

.dense-empty-line {
  margin: 6px 0;
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
  font-style: italic;
}

.session-brief-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
  padding: 6px 10px;
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-sm);
}

.trace-turns-scroll {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 480px;
  overflow-y: auto;
  padding-right: 4px;
}

.turn-fold-item {
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  overflow: hidden;
  background: var(--drama-color-surface-alt);
}

.turn-fold-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: var(--drama-font-size-sm, 14px);
  cursor: pointer;
  user-select: none;
}

.turn-head-l {
  display: flex;
  align-items: center;
  gap: 6px;
}

.turn-tit {
  font-weight: 600;
}

.error-badge {
  font-size: var(--drama-font-size-xs, 12px);
  padding: 2px 8px;
  border-radius: var(--drama-radius-xs, 4px);
  background: rgba(239, 68, 68, 0.12);
  color: var(--drama-color-danger);
}

.turn-head-r {
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
  font-family: var(--drama-font-family-mono);
}

.turn-steps-list {
  display: flex;
  flex-direction: column;
  background: var(--drama-color-surface);
  border-top: 1px solid var(--drama-color-border);
}

.step-dense-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 10px;
  border-bottom: 1px solid color-mix(in srgb, var(--drama-color-border) 40%, transparent);
  font-size: var(--drama-font-size-xs, 12px);
}

.step-dense-row:last-child {
  border-bottom: none;
}

.step-line-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.step-seq {
  font-family: var(--drama-font-family-mono);
  font-weight: 700;
  color: var(--drama-color-text-muted);
  width: 26px;
}

.step-kind-name {
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs, 12px);
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.step-code-pip {
  font-size: var(--drama-font-size-xs, 12px);
  padding: 2px 8px;
  border-radius: var(--drama-radius-xs, 4px);
}

.step-code-pip.is-ok {
  background: rgba(34, 197, 94, 0.1);
  color: var(--drama-color-success);
}

.step-code-pip.is-fail {
  background: rgba(239, 68, 68, 0.1);
  color: var(--drama-color-danger);
}

.step-files-strip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-left: 34px;
  font-size: var(--drama-font-size-xs, 12px);
}

.files-k {
  color: var(--drama-color-text-muted);
}

.file-chip {
  padding: 2px 8px;
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-xs, 4px);
  font-size: var(--drama-font-size-xs, 12px);
}

.step-pre-box {
  margin: 6px 0 0 34px;
  padding: 8px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  font-size: var(--drama-font-size-xs, 12px);
  max-height: 180px;
  overflow: auto;
  font-family: var(--drama-font-family-mono);
}

.dense-events-accordion {
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  overflow: hidden;
  background: var(--drama-color-surface-alt);
}

.events-accordion-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: var(--drama-font-size-sm, 14px);
  cursor: pointer;
  user-select: none;
}

.acc-l {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--drama-color-text-muted);
}

.events-compact-list {
  max-height: 200px;
  overflow-y: auto;
  background: var(--drama-color-surface);
  border-top: 1px solid var(--drama-color-border);
}

.event-dense-item {
  display: grid;
  grid-template-columns: 80px 1fr 60px;
  gap: 6px;
  padding: 4px 10px;
  font-size: var(--drama-font-size-xs, 12px);
  border-bottom: 1px solid color-mix(in srgb, var(--drama-color-border) 40%, transparent);
  align-items: center;
}

.ev-time-txt {
  font-family: var(--drama-font-family-mono);
  color: var(--drama-color-text-muted);
}

.ev-tool-txt {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ev-res-txt {
  text-align: right;
}

.ev-res-txt.is-ok {
  color: var(--drama-color-success);
}

.ev-res-txt.is-run {
  color: var(--drama-color-info);
}
</style>

